import type { LandingPageAnalysis, LandingPageIssue, LandingPageRecommendation } from '@/types/landing-page-analysis';
import type { PageAudit, CompetitorAudit } from '@/types/sem';
import type { RestaurantSemData } from '@/types/sem';
import { normalizeRestaurantName, normalizeCampaignName } from '@/lib/normalize-names';

// === Types ===

export type SeverityLevel = 'critical' | 'warning' | 'ok';
export type AdStatus = 'performing' | 'underperforming';
export type LandingStatus = 'broken' | 'aligned';

export interface StoryData {
  // Masthead
  restaurantName: string;
  campaignName: string;
  domain: string;
  severity: SeverityLevel;

  // Revenue
  revenueLoss: number;
  monthlyClicks: number;
  portfolioAvgCvr: number;
  pageCvr: number;
  avgConvValue: number;

  // Step 1: Ad
  adHeadline: string;
  ctr: number;
  clicks: number;
  cpc: number;
  topKeywords: string[];
  adStatus: AdStatus;

  // Step 2: Landing
  pageH1: string;
  scores: {
    messageMatch: number;
    conversionOptimization: number;
    visualQuality: number;
    layoutScore: number;
  };
  issues: LandingPageIssue[];
  landingStatus: LandingStatus;

  // Step 3: Impact
  qualityScore: number;
  wastedSpend: number;
  clicksLostPct: number;

  // Step 4: Fixes
  recommendations: LandingPageRecommendation[];
  projectedCvr: number;
  projectedQualityScore: number;

  // Audit data (optional — only present after audit pipeline runs)
  pageAudit?: PageAudit;
  competitorAudit?: CompetitorAudit;
}

// === Calculations ===

export function calcRevenueLoss(
  portfolioAvgCvr: number,
  pageCvr: number,
  monthlyClicks: number,
  avgConvValue: number
): number {
  const cvrGap = portfolioAvgCvr - pageCvr;
  if (cvrGap <= 0) return 0;
  return Math.round(cvrGap * monthlyClicks * avgConvValue);
}

export function calcWastedSpend(
  monthlyClicks: number,
  cpc: number,
  pageCvr: number,
  portfolioAvgCvr: number
): number {
  if (portfolioAvgCvr === 0) return 0;
  const wasteFraction = 1 - pageCvr / portfolioAvgCvr;
  if (wasteFraction <= 0) return 0;
  return Math.round(monthlyClicks * cpc * wasteFraction);
}

export function calcClicksLostPct(pageCvr: number, portfolioAvgCvr: number): number {
  if (portfolioAvgCvr === 0) return 0;
  return Math.round((1 - pageCvr / portfolioAvgCvr) * 100);
}

// === Thresholds ===

export function getSeverity(revenueLoss: number, messageMatch: number): SeverityLevel {
  if (revenueLoss > 2000 || messageMatch < 40) return 'critical';
  if (revenueLoss >= 500 || messageMatch < 70) return 'warning';
  return 'ok';
}

export function getAdStatus(ctr: number): AdStatus {
  return ctr >= 2.0 ? 'performing' : 'underperforming';
}

export function getLandingStatus(messageMatch: number): LandingStatus {
  return messageMatch < 50 ? 'broken' : 'aligned';
}

export function isScoreLow(score: number): boolean {
  return score < 40;
}

// === Data Assembly ===

export function buildStoryData(
  page: LandingPageAnalysis,
  restaurant: RestaurantSemData,
  allRestaurants: RestaurantSemData[]
): StoryData {
  const sem = restaurant.data;
  const summary = sem.summary;

  // Find matching campaign/ad group for ad headline
  const campaign = sem.campaigns.find(c => c.id === page.campaignId);
  const adGroup = campaign?.adGroups?.find(ag => ag.id === page.adGroupId);
  const bestAd = adGroup?.ads?.[0];
  const bestHeadline = bestAd?.headlines?.find(h =>
    h.performanceRating === 'best' || h.performanceRating === 'good'
  );
  const adHeadline = bestHeadline?.text
    || bestAd?.headlines?.[0]?.text
    || campaign?.name
    || 'Ad Copy';

  // Portfolio avg CVR
  const portfolioAvgCvr = calcPortfolioAvgCvr(allRestaurants);

  // Page CVR (use restaurant-level if no page-specific data)
  const pageCvr = summary.clicks > 0
    ? (summary.conversions / summary.clicks) * 100
    : 0;

  // Avg conversion value
  const avgConvValue = summary.conversions > 0
    ? summary.conversionValue / summary.conversions
    : 0;

  const monthlyClicks = summary.clicks;
  const cpc = summary.cpc;
  const revenueLoss = calcRevenueLoss(portfolioAvgCvr / 100, pageCvr / 100, monthlyClicks, avgConvValue);

  // Top keywords
  const topKeywords = (sem.keywords || [])
    .sort((a, b) => (b.metrics?.clicks || 0) - (a.metrics?.clicks || 0))
    .slice(0, 3)
    .map(k => k.keyword);

  // H1 from SEO analysis
  const pageH1 = page.seoAnalysis?.h1?.text || 'No H1 detected';

  // Domain from URL
  let domain = '';
  try { domain = new URL(page.url).hostname; } catch { domain = page.url; }

  const messageMatch = page.scores.messageMatch;
  const severity = getSeverity(revenueLoss, messageMatch);

  const pageAudit = restaurant.pageAudit;
  const competitorAudit = restaurant.competitorAudit;

  return {
    restaurantName: normalizeRestaurantName(restaurant.name),
    campaignName: normalizeCampaignName(campaign?.name || 'Campaign'),
    domain,
    severity,
    revenueLoss,
    monthlyClicks,
    portfolioAvgCvr,
    pageCvr,
    avgConvValue,
    adHeadline,
    ctr: summary.ctr,
    clicks: monthlyClicks,
    cpc,
    topKeywords,
    adStatus: getAdStatus(summary.ctr),
    pageH1,
    scores: {
      messageMatch: page.scores.messageMatch,
      conversionOptimization: page.scores.conversionOptimization,
      visualQuality: page.scores.visualQuality,
      layoutScore: page.scores.layoutScore,
    },
    issues: page.issues.filter(i => i.severity === 'critical' || i.severity === 'major').slice(0, 4),
    landingStatus: getLandingStatus(messageMatch),
    qualityScore: summary.qualityScoreAvg || 0,
    wastedSpend: calcWastedSpend(monthlyClicks, cpc, pageCvr / 100, portfolioAvgCvr / 100),
    clicksLostPct: calcClicksLostPct(pageCvr, portfolioAvgCvr),
    recommendations: page.recommendations.filter(r => r.priority === 'high').slice(0, 3),
    projectedCvr: portfolioAvgCvr,
    projectedQualityScore: messageMatch > 70 ? 7 : messageMatch > 50 ? 6 : 5,
    pageAudit,
    competitorAudit,
  };
}

function calcPortfolioAvgCvr(restaurants: RestaurantSemData[]): number {
  const valid = restaurants.filter(r => r.data.summary.clicks > 0);
  if (valid.length === 0) return 0;
  const totalCvr = valid.reduce((sum, r) => {
    return sum + (r.data.summary.conversions / r.data.summary.clicks) * 100;
  }, 0);
  return totalCvr / valid.length;
}

// === Formatters ===

export function fmtMoney(n: number): string {
  return '$' + Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function fmtPct(n: number): string {
  return n.toFixed(1) + '%';
}

export function fmtNum(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
